import { useMutation, useQueryClient } from "@tanstack/react-query";
import { UPDATE_LADDER_PROGRESS } from "../api/queries";
import { useErrorReporter } from "./use-error-reporter";
import { graphQLClient } from "../graphqlClient";
import { useProductFeatures } from "./useProductFeatures";

const updateLadderProgress = async (ladderProgress) => {
  const {
    ladderId,
    productCode,
    numberOfSessions,
    highScore,
    maxScore,
    learnositySessionId,
    learnosityReference,
    externalRegistrationId,
    overallStatus,
    completeDate,
  } = ladderProgress;

  return graphQLClient.request(UPDATE_LADDER_PROGRESS, {
    input: {
      ladderId,
      productCode,
      numberOfSessions,
      highScore,
      maxScore,
      learnositySessionId,
      learnosityReference,
      externalRegistrationId,
      overallStatus,
      completeDate,
    },
  });
};

export const useUpdateLadderProgress = () => {
  const { reportError } = useErrorReporter(
    "Could not update the Ladder progress",
  );
  const queryClient = useQueryClient();
  const { activeProduct } = useProductFeatures();
  const mutation = useMutation({
    mutationFn: updateLadderProgress,
    onError: reportError,
    retry: 3,
  });

  const setLadderProgress = async (ladderProgress) => {
    const productCode = ladderProgress.productCode ?? activeProduct?.code;
    if (!productCode) {
      console.error(
        "Cannot update ladder progress without an active product code",
      );
      return false;
    }

    try {
      // Ladder progress is written to the per-product progress record
      // (prg-<productCode>), created lazily server-side on first write.
      await mutation.mutateAsync({ ...ladderProgress, productCode });

      const targetLadderId = String(ladderProgress.ladderId);

      // Update the cached progress record in place, then invalidate to
      // re-sync with the server in the background.
      queryClient.setQueryData(["myProgress", productCode], (progress) => {
        if (!progress) return progress;
        const existingProgress = progress.ladderProgress || [];

        const newLadderProgress = existingProgress.map((entry) =>
          String(entry.ladderId) === targetLadderId ? ladderProgress : entry,
        );
        if (
          !newLadderProgress.find(
            (entry) => String(entry.ladderId) === targetLadderId,
          )
        ) {
          newLadderProgress.push(ladderProgress);
        }

        return { ...progress, ladderProgress: newLadderProgress };
      });
      await queryClient.invalidateQueries({
        queryKey: ["myProgress", productCode],
      });
    } catch (error) {
      console.error("Error updating ladder progress:", error);
      return false;
    }
  };

  return { setLadderProgress };
};
